import type { ContractStatus } from './types'
import type { PageSlug } from './routes'

/** The five pages that carry the Mambu MCP / Mambu Agents tabs — the pages
 * the status matrix covers. Monthly and Tenant Limits have no product tabs
 * and are not in it. Named by route slug (lib/routes.ts), the same names the
 * checked-in matrix (productViewMatrix.json) and the backend status guards
 * (services/status_guards.py) use. */
export type ProductPage = Extract<
  PageSlug,
  'subscription' | 'additional-volume' | 'excess-volume' | 'user-limits' | 'usage'
>

/** What a product tab shows (MS-819; spec §4.1b):
 * - `full`: the page itself.
 * - `trial`: the page in its trial state (Subscription: + trial banner;
 *   Additional Volume: the locked purchase card, no simulator, no history).
 * - `expired`: the Expired Subscription view (status + em dashes, no balance).
 * - `preview`: the product's "Contact us" preview card. */
export type ProductView = 'full' | 'trial' | 'expired' | 'preview'

/** The status → view matrix: one page, one status, one view.
 * Ports the prototype's `variantFor(section, status)` (the live call chain is
 * `variantFor` ← `scenarioSuffix` ← `applyScenarioTabs` ← `doLogin`) section
 * for section — `topup` is Subscription, `volume` Additional Volume, `excess`
 * Excess Volume, `limits` User Limits, `usage` Usage — and matches the
 * ticket's 35-cell table (productViewMatrix.json) cell for cell.
 *
 * The product is not an input: the matrix is the same for both products, and
 * each tab passes its own product's status (an account can be Contracted on
 * one and Trial on the other). Not Contracted Mambu MCP — a cell the ticket
 * table does not have (spec §4.1b-mcp) — maps to the preview card like Mambu
 * Agents does (MS-819 Q2/Q13, decided by the PO 2026-10-07;
 * ARCHITECTURE_DECISIONS.md §42). */
const VIEW_BY_PAGE: Record<ProductPage, Record<ContractStatus, ProductView>> = {
  // Expired stays on the page, rendered as status + em dashes.
  subscription: { Trial: 'trial', Contracted: 'full', Expired: 'expired', 'Not Contracted': 'preview' },
  // Cannot buy without a contract: Expired is the preview too.
  'additional-volume': { Trial: 'trial', Contracted: 'full', Expired: 'preview', 'Not Contracted': 'preview' },
  // Excess needs a live contract; anything else is the preview (§4.1b-excess).
  'excess-volume': { Trial: 'preview', Contracted: 'full', Expired: 'preview', 'Not Contracted': 'preview' },
  'user-limits': { Trial: 'full', Contracted: 'full', Expired: 'preview', 'Not Contracted': 'preview' },
  // An expired product can still track what it consumed.
  usage: { Trial: 'full', Contracted: 'full', Expired: 'full', 'Not Contracted': 'preview' },
}

export function resolveProductView(page: ProductPage, status: ContractStatus): ProductView {
  return VIEW_BY_PAGE[page][status]
}

/** The translated label of each wire status (`subscription.status.*`). The
 * wire values themselves never change (ARCHITECTURE_DECISIONS.md §18); only
 * what is printed is translated. */
export const STATUS_LABEL_KEY = {
  Trial: 'subscription.status.trial',
  Contracted: 'subscription.status.contracted',
  Expired: 'subscription.status.expired',
  'Not Contracted': 'subscription.status.notContracted',
} as const satisfies Record<ContractStatus, string>
